import { FunctionComponent, h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { CloseIconSlim, HelpIcon } from '../utils/icons';
import formatCurrency from '../../../services/currency';
import { Trans } from '@lingui/macro';

// --- TYPE DEFINITIONS ---

export interface Winner {
	censoredUsername: string;
	amount: number;
}

interface WinnerRowProps {
	winner: Winner;
	position: number;
}

interface RoyalWinModalProps {
	winners: Winner[];
	onClose: () => void;
}

// --- MOCK DATA ---

const mockWinners: Winner[] = [
	{ censoredUsername: '9********0', amount: 5000 },
	{ censoredUsername: '9********4', amount: 5000 },
	{ censoredUsername: '9********2', amount: 5000 },
	{ censoredUsername: '7********2', amount: 5000 },
	{ censoredUsername: '1********2', amount: 5000 },
	{ censoredUsername: '5********2', amount: 5000 },
	{ censoredUsername: '1********1', amount: 5000 },
	{ censoredUsername: '1********9', amount: 5000 },
	{ censoredUsername: '7********6', amount: 5000 },
	{ censoredUsername: '9********4', amount: 5000 },
	{ censoredUsername: '9********2', amount: 5000 },
	{ censoredUsername: '7********2', amount: 5000 },
	{ censoredUsername: '1********2', amount: 5000 },
	{ censoredUsername: '5********2', amount: 5000 },
	{ censoredUsername: '1********1', amount: 5000 },
	{ censoredUsername: '1********9', amount: 5000 },
	{ censoredUsername: '7********6', amount: 5000 },
];

// --- SUB-COMPONENTS ---

export const PromoHeader = ({ onClose, children }: { onClose: () => void, children: preact.ComponentChildren }) => (
	<div class="relative flex-none text-center p-4">
		<button onClick={onClose} class="absolute top-4 right-4 text-white">
			<CloseIconSlim class="w-6 h-6" />
		</button>
		<div class="flex items-center justify-start gap-4 px-2">
			<div class="w-24 h-12 relative"><img class="absolute w-full" src={require("url:../assets/img/toto-promo/victory-vault.svg")} /></div>
			<div class="px-4">{children}</div>
		</div>
	</div>
);

export const WinnerRow = ({ winner, position }: WinnerRowProps) => (
	<div class="flex items-center text-white p-2 border-b border-harif-700">
		<div class="w-8 text-lg font-bold text-custom-orange">{position}</div>
		<div class="grow">{winner.censoredUsername}</div>
		<div class="text-lg font-bold text-custom-orange">{formatCurrency(winner.amount)}</div>
	</div>
);

export const WinnersList = ({ winners }: { winners: Winner[] }) => (
	<div class="grow p-4 flex space-x-2 flex-col">
		<h2 class="text-center text-white font-bold mb-2 uppercase">Yesterday's Winners</h2>
		<div class="grow overflow-y-auto scrollbar-fancy scrollbar-fancy-better max-h-[40vh]">
			{winners.map((winner, index) => (
				<WinnerRow key={winner.censoredUsername + '-' + index} winner={winner} position={index + 1} />
			))}
		</div>
	</div>
);

export const PromoFooter = ({ children }) => (
	<div class="flex-none p-4 flex items-center justify-center space-x-2">
		{children}
	</div>
);


export function HelpButton({ onClick }: { onClick?: () => void }) {
	return <button class="p-3 bg-harif-950 rounded-lg text-blue-whale-900" onClick={onClick}>
		<HelpIcon class="w-6 h-6 fill-white" />
	</button>
}

export function JoinButton({ onClick }: { onClick?: () => void }) {
	return <button class="grow p-3 bg-custom-orange text-harif-800 font-bold rounded-lg uppercase" onClick={onClick}>
		<Trans>Join now</Trans>
	</button>
}

export const RoyalWinOverlay = ({ onClose, children }: { onClose: () => void, children: preact.ComponentChildren }) => {
	const modalRef = useRef<HTMLDivElement>(null);

	const handleClickOutside = (event: MouseEvent) => {
		if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
			onClose();
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	return (
		<div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div ref={modalRef} class="relative w-full max-w-md bg-linear-to-b from-harif-900 to-harif-800 rounded-lg shadow-lg flex flex-col">
				{children}
			</div>
		</div>
	);
}
// --- MAIN COMPONENT ---

export const RoyalWinModal = ({ winners, onClose }: RoyalWinModalProps) => {
	return (
		<RoyalWinOverlay onClose={onClose}>
			<CloverIcon class="absolute top-10 right-10 w-16 h-16 text-blue-whale-600 opacity-20" />
			<PromoHeader onClose={onClose}>
				<div class="text-white">
					<div class="text-3xl font-bold text-altbrand-400">100,000,000</div>
					<div class="text-xs">200 PLAYERS x 5,000 EACH x 100 DAYS!</div>
				</div>
			</PromoHeader>
			<WinnersList winners={winners} />
			<PromoFooter>
				<HelpButton />
				<JoinButton />
			</PromoFooter>
		</RoyalWinOverlay>
	);
};

// --- EXAMPLE USAGE ---

export const RoyalWinModalExample = () => {
	const handleClose = () => console.log("Close button clicked");
	return <RoyalWinModal winners={mockWinners} onClose={handleClose} />
}



export const RoyalWinLogo = ({ ...props }) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 40" {...props}>
	<text y="30" font-family="Arial" font-size="20" fill="white">
		<tspan x="0" dy="0">ROYAL</tspan>
		<tspan x="0" dy="10">WIN</tspan>
	</text>
</svg>

export const CloverIcon = ({ ...props }) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
	<path fill="currentColor" d="M14,2A2,2 0 0,1 16,4C16,5.11 15.1,6 14,6C13.5,6 13,5.8 12.5,5.5C12.2,6.2 12,7.1 12,8C12,8.9 12.2,9.8 12.5,10.5C13,10.2 13.5,10 14,10A2,2 0 0,1 16,12C16,13.11 15.1,14 14,14C13.5,14 13,13.8 12.5,13.5C12.2,14.2 12,15.1 12,16C12,17.1 12.9,18 14,18A2,2 0 0,1 16,20C16,21.11 15.1,22 14,22C12.9,22 12,21.1 12,20C12,19.1 12.2,18.2 12.5,17.5C12,17.2 11.5,17 11,17A2,2 0 0,1 9,15C9,14.5 9.2,14 9.5,13.5C8.8,13.2 8,12.9 8,12C8,11.1 8.2,10.2 8.5,9.5C8,9.2 7.5,9 7,9A2,2 0 0,1 5,7C5,6.5 5.2,6 5.5,5.5C4.8,5.2 4,4.9 4,4A2,2 0 0,1 6,2C7.1,2 8,2.9 8,4C8,4.9 7.8,5.8 7.5,6.5C8,6.8 8.5,7 9,7A2,2 0 0,1 11,9C11,9.5 10.8,10 10.5,10.5C11.2,10.8 12,11.1 12,12C12,12.9 11.8,13.8 11.5,14.5C12,14.8 12.5,15 13,15A2,2 0 0,1 15,13C15,12.5 14.8,12 14.5,11.5C15.2,11.2 16,10.9 16,10C16,9.1 15.8,8.2 15.5,7.5C16,7.2 16.5,7 17,7A2,2 0 0,1 19,9C19,9.5 18.8,10 18.5,10.5C19.2,10.8 20,11.1 20,12C20,13.11 19.1,14 18,14A2,2 0 0,1 16,12A2,2 0 0,1 18,10C18,8.9 17.1,8 16,8C15.5,8 15,8.2 14.5,8.5C14.2,7.8 14,6.9 14,6A2,2 0 0,1 12,4A2,2 0 0,1 14,2Z" />
</svg>
